// src/App.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";

import Cursor from "./components/Cursor";
import NavDots from "./components/NavDots";
import HomePage from "./components/HomePage";
import WorkExpPage from "./components/WorkExpPage";
import AboutMePage from "./components/AboutMePage";
import ContactPage from "./components/ContactPage";
import { playClickSound } from "./util/playClickSound";

const TOTAL_PAGES = 4;

function App() {
  const [currentPage, setCurrentPage] = useState(0);
  const [logoClicks, setLogoClicks] = useState(0);
  const isScrolling = useRef(false);
  const touchStartX = useRef(0);
  const wheelAccumulator = useRef(0);
  const wheelResetTimer = useRef(null);
  const containerRef = useRef(null);

  const goToPage = useCallback((pageIndex) => {
    if (pageIndex >= 0 && pageIndex < TOTAL_PAGES) {
      setCurrentPage(pageIndex);
    }
  }, []);

  // Logo click easter egg
  const handleLogoClick = () => {
    setLogoClicks((prev) => prev + 1);
  };

  useEffect(() => {
    const handler = (e) => {
      if (
        e.target.matches(
          ".nav-link, .gallery-nav, .location-tag, .social-link, .social-icon, .cta-button"
        )
      ) {
        playClickSound();
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  // Block pinch-zoom, ctrl/cmd+wheel zoom, and double-tap zoom on mobile
  useEffect(() => {
    const preventPinch = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const preventMultiTouchMove = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const preventCtrlWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };

    let lastTouchEnd = 0;
    const preventDoubleTap = (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    document.addEventListener("touchstart", preventPinch, { passive: false });
    document.addEventListener("touchmove", preventMultiTouchMove, {
      passive: false,
    });
    document.addEventListener("wheel", preventCtrlWheel, { passive: false });
    document.addEventListener("touchend", preventDoubleTap, { passive: false });
    // iOS Safari gesture events
    const gestureStart = (e) => e.preventDefault();
    const gestureChange = (e) => e.preventDefault();
    const gestureEnd = (e) => e.preventDefault();
    document.addEventListener("gesturestart", gestureStart, { passive: false });
    document.addEventListener("gesturechange", gestureChange, {
      passive: false,
    });
    document.addEventListener("gestureend", gestureEnd, { passive: false });

    return () => {
      document.removeEventListener("touchstart", preventPinch);
      document.removeEventListener("touchmove", preventMultiTouchMove);
      document.removeEventListener("wheel", preventCtrlWheel);
      document.removeEventListener("touchend", preventDoubleTap);
      document.removeEventListener("gesturestart", gestureStart);
      document.removeEventListener("gesturechange", gestureChange);
      document.removeEventListener("gestureend", gestureEnd);
    };
  }, []);

  useEffect(() => {
    if (logoClicks === 3) {
      document.body.classList.add("rainbow-body");
      const timer = setTimeout(() => {
        document.body.classList.remove("rainbow-body");
        setLogoClicks(0);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [logoClicks]);

  // Event listeners for navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        goToPage(currentPage + 1);
      } else if (e.key === "ArrowLeft") {
        goToPage(currentPage - 1);
      }
    };

    const handleWheel = (e) => {
      // Accumulate wheel deltas to treat trackpad gestures as a single navigation
      if (e.cancelable) {
        e.preventDefault();
      }

      if (isScrolling.current) return;

      const delta = e.deltaY || 0;
      wheelAccumulator.current += delta;

      // Reset accumulator if there's a pause in wheel events (end of gesture)
      if (wheelResetTimer.current) {
        clearTimeout(wheelResetTimer.current);
      }
      wheelResetTimer.current = setTimeout(() => {
        wheelAccumulator.current = 0;
      }, 200);

      const threshold = 120; // Higher threshold to avoid accidental multi-page scrolls
      if (Math.abs(wheelAccumulator.current) >= threshold) {
        if (wheelAccumulator.current > 0) {
          goToPage(currentPage + 1);
        } else {
          goToPage(currentPage - 1);
        }
        isScrolling.current = true;
        wheelAccumulator.current = 0;

        // Fallback unlock in case no transitionend is fired
        setTimeout(() => {
          isScrolling.current = false;
        }, 700);
      }
    };

    const handleTouchStart = (e) => {
      touchStartX.current = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX.current - touchEndX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          goToPage(currentPage + 1); // Swipe left
        } else {
          goToPage(currentPage - 1); // Swipe right
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
      if (wheelResetTimer.current) {
        clearTimeout(wheelResetTimer.current);
        wheelResetTimer.current = null;
      }
    };
  }, [currentPage, goToPage]);

  return (
    <>
      <Cursor />
      <NavDots
        totalPages={TOTAL_PAGES}
        currentPage={currentPage}
        goToPage={goToPage}
      />

      <div
        ref={containerRef}
        className="container"
        onTransitionEnd={(e) => {
          if (e.target === e.currentTarget && e.propertyName === "transform") {
            isScrolling.current = false;
          }
        }}
        style={{ transform: `translateX(-${currentPage * 100}vw)` }}
      >
        <HomePage goToPage={goToPage} onLogoClick={handleLogoClick} />
        <AboutMePage goToPage={goToPage} isActive={currentPage === 1} />
        <WorkExpPage isActive={currentPage === 2} />
        <ContactPage isActive={currentPage === 3} />
      </div>
    </>
  );
}

export default App;
